import React, { useState, useEffect, useRef, useCallback } from "react";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import { TextInput, EmailInput, SelectInput } from "../../Components";

export const GenderItem = (props) => {
  const [checked, setChecked] = useState(
    props.form.data.gender === props.itemData.id
  );

  useEffect(() => {
    setChecked(props.form.data.gender === props.itemData.id);
  }, [props.form.data.gender, props.itemData.id]);

  const onChange = (e) => {
    props.setShowConfirmInfModal(true);
    props.updateForm({
      data: {
        ...props.form.data,
        gender: e.target.id,
      },
    });
  };

  return (
    <>
      <label
        htmlFor={props.itemData.id}
        className={`flex items-center justify-center p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group text-xs sm:text-sm
        ${
          checked
            ? "border-green-500 bg-green-50 shadow-md scale-105"
            : "border-gray-200 hover:border-green-300 hover:bg-green-50"
        }`}
      >
        <input
          name="gender"
          id={props.itemData.id}
          checked={checked}
          onChange={onChange}
          type="radio"
          className="sr-only"
        />
        <div
          className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 mr-2 sm:mr-3 flex items-center justify-center transition-all
        ${
          checked
            ? "border-green-500 bg-green-500"
            : "border-gray-400 group-hover:border-green-500"
        }`}
        >
          {checked && (
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white"></div>
          )}
        </div>
        <span
          className={`font-medium transition-colors whitespace-nowrap
        ${checked ? "text-green-600" : "text-gray-700"}`}
        >
          {props.itemData.value}
        </span>
      </label>
    </>
  );
};

export default function MyProfileInfo({
  setShowConfirmInfModal,
  error,
  form,
  setInput,
  updateForm,
  countries,
  genderList,
}) {
  console.log("🚀 ~ MyProfileInfo ~ form:", form);

  const phoneInputRef = useRef(null);
  const itiInstance = useRef(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    const input = phoneInputRef.current;
    if (!input || isInitialized.current) return;

    itiInstance.current = intlTelInput(input, {
      initialCountry: "us",
      geoIpLookup: (cb) => {
        fetch("https://ipapi.co/json/")
          .then((res) => res.json())
          .then((data) => cb(data.country_code))
          .catch(() => cb("us"));
      },
      utilsScript:
        "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
      separateDialCode: true,
      preferredCountries: ["us", "gb", "in", "au"],
    });

    if (form.data.phonenumber) {
      itiInstance.current.setNumber(form.data.phonenumber);
    }

    isInitialized.current = true;

    const handlePhoneChange = () => {
      if (!itiInstance.current || !phoneInputRef.current) return;

      const countryData = itiInstance.current.getSelectedCountryData();
      const phoneNumber = phoneInputRef.current.value;

      if (phoneNumber) {
        const fullPhoneNumber = `+${countryData.dialCode}${phoneNumber}`;

        updateForm({
          data: {
            ...form.data,
            phonenumber: fullPhoneNumber,
          },
        });
      } else {
        updateForm({
          data: {
            ...form.data,
            phonenumber: "",
          },
        });
      }
    };

    input.addEventListener("input", handlePhoneChange);
    input.addEventListener("countrychange", handlePhoneChange);

    return () => {
      input.removeEventListener("input", handlePhoneChange);
      input.removeEventListener("countrychange", handlePhoneChange);
      if (isInitialized.current) {
        itiInstance.current?.destroy();
        isInitialized.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (itiInstance.current && isInitialized.current && form.data.phonenumber) {
      const currentNumber = itiInstance.current.getNumber();
      if (currentNumber !== form.data.phonenumber) {
        itiInstance.current.setNumber(form.data.phonenumber);
      }
    }
  }, [form.data.phonenumber]);

  const handlePhoneBlur = useCallback(() => {
    if (form.data.phonenumber) {
      updateForm({
        data: {
          ...form.data,
          phonenumber: form.data.phonenumber,
        },
      });
    }
  }, [form.data, updateForm]);

  const selectCountryList = countries
    ? countries.map((country) => {
        return { id: country["id"], value: country["name"] };
      })
    : [];

  const selectedCountry = React.useMemo(() => {
    if (form.data.country_id !== "" && form.data.country_id !== null) {
      return (
        selectCountryList.find((item) => {
          return String(item.id) === String(form.data.country_id);
        }) || null
      );
    }
    return null;
  }, [form.data.country_id, selectCountryList]);

  const changeMedium = (key) => (item) => {
    setShowConfirmInfModal(true);
    updateForm({
      data: {
        ...form.data,
        [key]: item,
      },
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const handleDateChange = (e) => {
    setShowConfirmInfModal(true);
    const selectedDate = e.target.value;
    updateForm({ dateofbirth: selectedDate });
  };

  return (
    <div className="space-y-6">
      {/* Basic Information Section */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <TextInput
            value={form.data.firstName}
            type="text"
            onChange={setInput("firstName")}
            label="First name"
            error={error && form.data.firstName === ""}
            errorText="Please enter First name"
            autoComplete="given-name"
            isRequired
            placeholder="Enter your first name"
            className={`!shadow-md ${
              error && form.data.firstName === ""
                ? "!border-red-500 !bg-red-50"
                : "!border-green-500 !bg-green-50"
            }`}
          />

          <TextInput
            value={form.data.lastName}
            type="text"
            onChange={setInput("lastName")}
            label="Last name"
            autoComplete="family-name"
            // error={error && form.data.lastName === ""}
            errorText="Please enter Last name"
            placeholder="Enter your last name"
            className={`!shadow-md !border-green-500 !bg-green-50`}
          />
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <EmailInput
            value={form.data.email}
            type="email"
            onChange={setInput("email")}
            label="Email address"
            error={error && form.data.email === ""}
            errorText="Please enter valid email"
            isRequired
            placeholder="your.email@example.com"
            className={`!shadow-md ${
              error && form.data.email === ""
                ? "!border-red-500 !bg-red-50"
                : "!border-green-500 !bg-green-50"
            }`}
          />

          {/* Phone Number Input */}
          <div className="flex flex-col">
            <label className="mb-1 font-bold text-sm text-gray-700 text-start">
              Phone Number
              {error && form.data.phonenumber === "" && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <input
              ref={phoneInputRef}
              type="tel"
              className={`
                w-full p-[9px] border rounded-md 
                shadow-[0px_1px_2px_0px_rgba(16,24,40,0.04)] placeholder:text-[17px]
                ${
                  error && form.data.phonenumber === ""
                    ? "!border-red-500 bg-[#fff2f4] focus:ring-red-500"
                    : "!border-green-500 !bg-green-50"
                }
              `}
              placeholder="Enter phone number"
              onBlur={handlePhoneBlur}
            />
            {error && form.data.phonenumber === "" && (
              <p className="text-red-500 text-xs flex items-center gap-2 mt-2">
                Please enter phone number
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Personal Details Section */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Date of Birth - Updated Input */}
          <div className="space-y-2">
            <div className="text-left">
              <label
                className=" text-sm mb-2 text-[#1A2435] text-left font-bold "
                htmlFor="dateofbirth"
              >
                Date of birth
                {error && !form.dateofbirth && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <div
                className={`
                border p-[8px] mt-1 rounded-md cursor-pointer shadow-md transition-colors duration-200
                ${
                  error && !form.dateofbirth
                    ? "border-red-500 bg-red-50"
                    : "border-green-500 bg-green-50 hover:border-green-600"
                }
              `}
              >
                <input
                  type="date"
                  id="dateofbirth"
                  name="dateofbirth"
                  value={formatDateForInput(form.dateofbirth)}
                  onChange={handleDateChange}
                  className="bg-transparent w-full outline-none cursor-pointer text-[#1A2435]"
                  max={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>

              {error && !form.dateofbirth && (
                <p className="text-red-500 text-xs flex items-center gap-2 mt-3">
                  Please enter Date of birth
                </p>
              )}
            </div>
          </div>

          {/* Country Selection */}
          <div className="space-y-2">
            <SelectInput
              label="Country"
              value={form.data.country_id}
              list={selectCountryList}
              selected={selectedCountry}
              onChange={changeMedium("country_id")}
              error={
                error &&
                (form.data.country_id === "" || form.data.country_id === 0)
              }
              errorText="Please select Country"
              isRequired
              className={`!shadow-md ${
                error && form.data.country_id === ""
                  ? "!border-red-500 !bg-red-50"
                  : "!border-green-500 !bg-green-50"
              }`}
            />
          </div>
        </div>
      </div>

      {/* Gender Selection */}
      <div className="space-y-3">
        <label className="flex items-center text-sm font-medium text-gray-700">
          Gender
          <span className="text-red-500 ml-1">*</span>
        </label>

        <div
          className={`p-4 rounded-xl transition-all duration-200 ${
            error && form.data.gender === ""
              ? "bg-red-50 ring-2 ring-red-500/20"
              : "bg-green-50/50"
          }`}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {genderList.map((item, index) => (
              <GenderItem
                itemData={item}
                key={index}
                updateForm={updateForm}
                form={form}
                setShowConfirmInfModal={setShowConfirmInfModal}
              />
            ))}
          </div>

          {error && form.data.gender === "" && (
            <p className="text-red-500 text-xs flex items-center gap-2 mt-3">
              Please select your gender
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
